/* eslint-disable import/order */
// Initialize i18next FIRST — before ErrorBoundary and everything it pulls in
// (e.g. the column/enum definitions in utils/constants that call i18n.t() at
// import time). Combined with synchronous init (initAsync: false in
// utils/i18n), those module-level lookups resolve real translations instead of
// their English defaults on first load.
import 'utils/i18n';
import { ErrorBoundary } from 'components/ErrorBoundary';
/* eslint-enable import/order */
import { Auth0Provider } from '@auth0/auth0-react';
import { App as AntdApp } from 'antd5';
import ReactDOM from 'react-dom';
import { Helmet } from 'react-helmet';
import { Route, Switch, useHistory } from 'react-router-dom';
import { LastLocationProvider } from 'react-router-last-location';

import { ImageNavButtonDisabledProvider } from 'components/damages/ImageNavButton.Context';
import { FileUploaderProvider } from 'components/FileUploader';
import {
  DropdownStyles,
  FormAndInputStyles,
  GlobalStyles,
  MessageStyles,
  PopoverStyles,
} from 'components/ui';
import { Unsupported } from 'components/Unsupported';
import { GenericProgressContextProvider } from 'horizon/components/GlobalProgressIndicator/GenericProgress/GenericProgressContext';
import { Router } from 'horizon/components/Router';
import { AccountContextProvider } from 'utils/account/AccountContextProvider';
import { ApolloClientProvider } from 'utils/apollo';
import { AUTH0_SCOPE, AuthContextProvider, Session } from 'utils/auth';
import { initAuthDiagnostics } from 'utils/auth/authDiagnostics';
import { Theme, Themes, ThemeStorageKey } from 'utils/color/horizonTheme';
import { ThemeProvider } from 'utils/color/ThemeProvider';
import { isIE } from 'utils/document';
import {
  AUTH0_CLIENT_ID,
  AUTH0_DOMAIN,
  CI_MERGE_REQUEST_IID,
  CI_PROJECT_ID,
  CI_PROJECT_PATH,
  HORIZON_ENDPOINT,
  isE2ETest,
  isLocal,
  isProdReview,
} from 'utils/env';
import { ReduxProvider } from 'utils/state';
import { getItem as getStorageItem } from 'utils/storage';

import Bootstrap from './Bootstrap';
import Maintenance from './components/Maintenance';
import { AuthRedirect, Logout } from './routes/Auth0';
// import { DevTools as JotaiDevTools } from 'jotai-devtools'; // Uncomment to enable jotai devtools

const showGitlabReview = CI_MERGE_REQUEST_IID && !isProdReview;

// FIXME sc-169532 - Temporary diagnostics for unexpected logouts, must run before the Auth0Provider mounts
initAuthDiagnostics();

// use localstorage for playwright tests
const cacheLocation = isE2ETest || isLocal ? 'localstorage' : 'memory';

function EntryPoint() {
  return (
    <Switch>
      <Route exact={true} path="/auth_redirect" component={AuthRedirect} />
      <Route exact={true} path="/logout" component={Logout} />
      <Route
        path="/"
        render={() => (
          <Session>
            <AccountContextProvider>
              <Bootstrap />
            </AccountContextProvider>
          </Session>
        )}
      />
    </Switch>
  );
}

function AuthProvider() {
  const { replace } = useHistory();
  const auth0RedirectUri =
    typeof window !== 'undefined' ? window.location.origin : HORIZON_ENDPOINT;

  return (
    <Auth0Provider
      authorizationParams={{
        audience: 'https://api.int.skyspecs.com/fleet/graphql',
        redirect_uri: auth0RedirectUri,
        scope: AUTH0_SCOPE,
      }}
      cacheLocation={cacheLocation}
      clientId={AUTH0_CLIENT_ID}
      domain={AUTH0_DOMAIN}
      onRedirectCallback={appState => {
        // `returnTo` and `state` are originally set in Session, but then sent
        // to `loginWithRedirect()` in AuthRedirect
        replace(appState?.returnTo || window?.location?.pathname || '/', {
          state: appState?.state ? JSON.parse(appState.state) : undefined,
        });
      }}
      useRefreshTokens={true}
      useRefreshTokensFallback={true} // If the refresh token fails, try to re-auth using silent session auth
    >
      <AuthContextProvider>
        <ApolloClientProvider>
          {/*<JotaiDevTools /> /!* uncomment to enable jotai devtools *!/*/}
          <EntryPoint />
        </ApolloClientProvider>
      </AuthContextProvider>
    </Auth0Provider>
  );
}

if (isIE()) {
  ReactDOM.render(<Unsupported />, document.getElementById('root'));
} else {
  ReactDOM.render(
    <AntdApp>
      <ThemeProvider theme={Themes[(getStorageItem(ThemeStorageKey) ?? Theme.Light) as Theme]}>
        <ErrorBoundary>
          <>
            <Helmet>
              {showGitlabReview && (
                <script
                  defer
                  src="https://gitlab.com/assets/webpack/visual_review_toolbar.js"
                  id="review-app-toolbar-script" // must be exactly this
                  data-project-id={CI_PROJECT_ID}
                  data-merge-request-id={CI_MERGE_REQUEST_IID}
                  data-project-path={CI_PROJECT_PATH}
                  data-require-auth
                  data-mr-url="https://gitlab.com"
                />
              )}
            </Helmet>
            <GenericProgressContextProvider>
              <FileUploaderProvider>
                {/* Possibly find a closer parent component that uses ImageNavButtonDisabledProvider
            this provider only controls disabling shortcutkeys when viewing an image that has
            an ImageNavButtons component.
            */}
                <ImageNavButtonDisabledProvider>
                  <DropdownStyles />
                  <FormAndInputStyles />
                  <GlobalStyles />
                  <MessageStyles />
                  <PopoverStyles />
                  <Router>
                    <Maintenance>
                      <LastLocationProvider>
                        <ReduxProvider>
                          <AuthProvider />
                        </ReduxProvider>
                      </LastLocationProvider>
                    </Maintenance>
                  </Router>
                </ImageNavButtonDisabledProvider>
              </FileUploaderProvider>
            </GenericProgressContextProvider>
          </>
        </ErrorBoundary>
      </ThemeProvider>
    </AntdApp>,
    document.getElementById('root')
  );
}
